import React from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";

interface NursingHomeMediaProps {
  centerName: string;
  nhSlug: string;
  imageUrl?: string;
  email?: string;
  county?: string;
}

export function NursingHomeMedia({ centerName, nhSlug, imageUrl, email, county }: NursingHomeMediaProps) {
  // Only show the image if a real imageUrl is provided
  const hasImage = !!imageUrl && imageUrl.trim() !== '';
  const { sendEmail } = useTransactionalEmail();
  const [emailSent, setEmailSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  if (!hasImage) {
    const handleRequestInfo = async () => {
      if (!email) return;
      setLoading(true);
      try {
        await sendEmail({
          type: 'info_request',
          data: {
            email: email,
            nursingHomeName: centerName,
            claimLink: `${window.location.origin}/nursing-homes/${nhSlug}?action=claim`,
            county: county || '',
          }
        });
        setEmailSent(true);
      } catch (e) {
        // Optionally handle error
      } finally {
        setLoading(false);
      }
    };
    return (
      <div className="flex flex-col items-center justify-center py-16 w-full">
        <img
          src="/lovable-uploads/e84bc991-1646-4a87-a71c-bf8ea2c08b33.png"
          alt="Friendly illustration of people talking in a care home"
          className="mb-6 w-full max-w-md"
        />
        <p className="text-lg text-gray-600 mb-4 text-center">
          This nursing home hasn't shared photos yet.
        </p>
        <Button onClick={handleRequestInfo} disabled={emailSent || loading}>
          {emailSent ? "Thanks, email sent" : loading ? "Sending..." : "Ask this nursing home to share photos"}
        </Button>
      </div>
    );
  }

  // Handle image URLs properly
  const getImageUrl = () => {
    // If the URL is already a full URL (including http or https), return it directly
    if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
      return imageUrl;
    }
    // If we have a local URL but it doesn't start with a slash, add one
    if (imageUrl && !imageUrl.startsWith('/')) {
      return `/${imageUrl}`;
    }
    // Return the local URL as is if it has a leading slash
    if (imageUrl) {
      return imageUrl;
    }
    return '';
  };

  const displayUrl = getImageUrl();

  return (
    <div className="bg-white py-6 border-b">
      <div className="container">
        <div className="w-full max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-lg">
            <AspectRatio ratio={16/9}>
              <img 
                src={displayUrl} 
                alt={`${centerName}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.error(`Error loading image for ${centerName}: ${displayUrl}`);
                  (e.target as HTMLImageElement).src = "/lovable-uploads/e84bc991-1646-4a87-a71c-bf8ea2c08b33.png";
                }}
              />
            </AspectRatio>
          </div>
        </div>
      </div>
    </div>
  );
}
